/* aw-cart-sync.js - VARIANT C FORK (2026-07-18, live-test rule 4)
 * Identical to theme B's aw-cart-sync.js except syncOffer(), which now
 * REPLACES the cart's discount-code set via the cart API instead of
 * appending via /discount/{code}. Appending let Shopify keep whichever
 * attached code paid the customer most (home77 -$77 beat family33 -$74.91
 * on 6 jug carts - Brian's 2026-07-18 report + Round 1 finding 6).
 * POST-TEST BACK-PORT CANDIDATE for B: never back-deploy mid-Round-1.
 *
 * Cart-side guardian for the water delivery flow. Three jobs:
 *  1. Keep the refundable bottle deposit quantity locked 1:1 to the water bottle
 *     quantity on ANY cart change. The change listener catches the drawer's
 *     quantity selects; a debounced MutationObserver on [data-products] catches
 *     everything else (remove links, +/- steppers, the theme's own re-renders).
 *     Corrections require a confirmed double-read: the legacy theme's own qty
 *     sequence writes deposits FIRST and water SECOND, so single snapshots lie.
 *  2. Own the drawer's "Renews at" line: published-curve math, shown only while
 *     a subscription line is in the cart. Never the legacy theme.js math
 *     ($24/bottle - the $48/$96/$144 quotes).
 *  3. Keep the auto-applied first-order discount code matched to the cart
 *     composition after cart edits (see syncOffer below).
 *
 * Re-renders go through the theme's own cart:build event so theme.js rebinds its
 * listeners; we never replace [data-products] innerHTML ourselves.
 * Deploy note: Rollouts serving contexts refresh asset digests only on a REAL
 * content change while the experiment is running (identical upserts dedupe;
 * events fired while paused are missed) - hence this line, 2026-07-17.
 */
const DEPOSIT_VARIANT_ID = 50052669866266; // One-Time Refundable Deposit (see theme-launch-admin-checklist.md)
const WATER_HANDLE = 'spring-water-delivery';
const DEPOSIT_HANDLES = ['refundable-jug-deposit', 'one-time-refundable-deposit'];
const DISPENSER_PRODUCT_ID = 8076964419; // 2.5 Gallon Dispenser

/* Shared cart transport (2026-08-07, add-to-cart 429 fix).
 * The singleton on window.__awCartNet is created by whichever of
 * aw-guided-flow.js / this file loads first; the full rationale and the measured
 * read-amplification trace live at the top of aw-guided-flow.js. Short version:
 * one tap of the order-page CTA put ~15 /cart.js requests on the wire, several
 * simultaneous, and the edge answered the losers with 429 + an HTML challenge
 * body. This module contributed 3 of those reads and could dispatch a second
 * 'cart:build', which is what makes the two installed apps re-read the cart.
 * Routing through the singleton means concurrent readers share ONE request and
 * every write is serialised. This file also ships on pages with no wizard, so
 * every helper falls back to a plain fetch of the same shape when the singleton
 * is absent. Keep in sync with aw-guided-flow.js. */
function awNet() { return window.__awCartNet || null; }

async function cartRead(fresh) {
  const n = awNet();
  if (n) return n.readSoft({ fresh: !!fresh });
  try {
    const r = await fetch('/cart.js');
    const t = await r.text();
    // /cart.js is text/javascript; an edge block is identifiable by an HTML body.
    if (!r.ok || t.trim().charAt(0) === '<') return null;
    return JSON.parse(t);
  } catch (_) { return null; }
}

async function cartPost(url, payload) {
  const n = awNet();
  if (n) return n.post(url, payload);
  try {
    const r = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return { ok: r.ok, status: r.status, transient: r.status === 429 || r.status >= 500 };
  } catch (_) { return { ok: false, status: 0, transient: true }; }
}

function cartAnnounce() {
  const n = awNet();
  if (n) return n.announce();
  try { document.dispatchEvent(new CustomEvent('cart:build')); } catch (_) {}
}

let timer = null;
let busy = false;
let dirtyWhileBusy = false; // mutations that land during a pass queue one follow-up
let settleChecked = false; // one bounded follow-up pass per trigger burst

/* Circuit breaker: this module should write to the cart a handful of times per
 * interaction at most. If some interplay with the legacy theme ever loops
 * (write -> re-render -> write), stop writing entirely for the rest of the
 * page view instead of fighting. Checkout enforcement still owns correctness. */
let writeStamps = [];
let tripped = false;
function breakerOk() {
  if (tripped) return false;
  const now = Date.now();
  writeStamps = writeStamps.filter((t) => now - t < 60000);
  if (writeStamps.length >= 10) {
    tripped = true;
    try { console.warn('[aw-cart-sync] write breaker tripped; standing down for this page view'); } catch (_) {}
    return false;
  }
  writeStamps.push(now);
  return true;
}

/* One customer-facing money format ("$104" / "$23.50"): flush dollar sign, two
 * decimals only when there are cents, no USD suffix. Mirrors aw-money.liquid and
 * usd() in aw-order-widget.js; keep the three in sync.
 * (The Subtotal row USED to be deliberately not ours. It is now - see
 * paintSubtotal() below, pre-launch audit IMPORTANT-1.) */
function usd(cents) {
  const n = cents / 100;
  const digits = n % 1 === 0 ? 0 : 2;
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: 2 });
}

function schedule(delay) {
  clearTimeout(timer);
  timer = setTimeout(reconcile, delay);
}

/* ---- Drawer SUBTOTAL (2026-08-07, pre-launch audit IMPORTANT-1) -------------
 *
 * THE DEFECT. The drawer rendered SUBTOTAL $313.00 on the DEFAULT 4-jug path
 * while /cart.js said 23600 and checkout charged $236.00. Also $425 vs $354.11
 * (6 jugs, family33) and $195 vs $166.62 (2-jug trial, starter22). It was exact
 * whenever no offer was attached ($348 vs 34800, 6 jugs no dispenser), which
 * pins the cause precisely: the figure is the pre-offer sum and the drawer never
 * subtracted the offer code's allocation.
 *
 * MECHANISM. The number is not computed here at all. templates/cart.ajax.liquid
 * publishes data-cart-subtotal on .cart__items and snippets/cart-drawer.liquid
 * server-renders {{ cart.total_price | money }}; legacy theme.js copies that into
 * [data-subtotal] on every re-render. Both are Liquid-side figures that do not
 * carry the cart-API discount allocation this theme attaches through syncOffer().
 * Editing the Liquid would not fix it - the value it reads is the wrong one, and
 * theme.js would overwrite the fix on the next render anyway.
 *
 * THE FIX. /cart.js total_price is the ONE figure verified equal to the checkout
 * charge, and it is already the number the wizard quotes. Take the drawer's
 * subtotal from there, and re-assert it whenever theme.js rewrites the row. The
 * savings line is filled from the same snapshot (original_total_price -
 * total_price, i.e. every discount Shopify itself counts, line-level and
 * cart-level), so a customer who sees a lower subtotal is told why instead of
 * being left to wonder.
 *
 * An over-quote is less dangerous than an under-quote, but it still contradicted
 * the card on the default path and was visible from every page in the store.
 *
 * Guarded against loops by idempotence, NOT by suppression: every write happens
 * only when the rendered text actually differs from the target, so the observer
 * callback our own write provokes finds nothing left to change and stops. (The
 * `painting` flag cannot do that job on its own - MutationObserver callbacks are
 * microtasks, so they run after the flag has already been cleared.) */
let lastCart = null;
let subObserver = null;
let painting = false;

function paintSubtotal() {
  const cart = lastCart;
  if (!cart || typeof cart.total_price !== 'number') return;
  painting = true;
  try {
    const target = usd(cart.total_price);
    document.querySelectorAll('[data-subtotal]').forEach((el) => {
      if (el.textContent.trim() !== target) el.textContent = target;
    });
    // Keep the attribute theme.js re-reads in step, so its next render starts
    // from the right number rather than reinstating the pre-offer one.
    document.querySelectorAll('[data-cart-subtotal]').forEach((el) => {
      if (el.getAttribute('data-cart-subtotal') !== String(cart.total_price)) {
        el.setAttribute('data-cart-subtotal', String(cart.total_price));
      }
    });
    const saved = Number(cart.original_total_price || 0) - Number(cart.total_price);
    document.querySelectorAll('[data-savings]').forEach((el) => {
      if (saved > 0) {
        const txt = "You're saving " + usd(saved);
        if (el.textContent.trim() !== txt) el.textContent = txt;
        el.classList.remove('hide');
      } else {
        if (el.textContent !== '') el.textContent = '';
        el.classList.add('hide');
      }
    });
  } catch (_) {
    /* non-blocking; checkout enforcement still owns correctness */
  } finally {
    painting = false;
  }
}

/* theme.js rebuilds the drawer footer (and the cart page) on every cart change
 * and writes its own figure back in. One observer over the drawer + cart page
 * re-asserts ours right after, on the same frame. */
function watchSubtotal() {
  if (subObserver) return;
  const found = [document.getElementById('CartDrawer'), document.querySelector('.cart__page'), document.querySelector('form[action*="/cart"]')].filter(Boolean);
  // The drawer's own form matches the third selector, so drop any root already
  // inside another - one subtree, one callback per mutation.
  const roots = found.filter((r) => !found.some((o) => o !== r && o.contains(r)));
  if (!roots.length) return;
  subObserver = new MutationObserver(() => {
    if (painting) return;
    // lastCart is dropped the moment anything signals a cart change, so this can
    // only ever re-assert a figure read AFTER the last change. A stale snapshot
    // would be the one thing worse than the bug: a total below the charge.
    paintSubtotal();
  });
  roots.forEach((r) => subObserver.observe(r, { childList: true, subtree: true, characterData: true }));
}

function noteCart(cart) {
  if (!cart) return cart;
  lastCart = cart;
  paintSubtotal();
  return cart;
}

/* Anything that signals the cart has moved drops the snapshot. Until the next
 * fresh read lands (reconcile, ~600ms later) the theme's own number stands - the
 * pre-existing over-quote at worst, never an under-quote from a stale figure. */
function invalidateCart() { lastCart = null; }

document.addEventListener('change', (e) => {
  if (!e.target.classList || !e.target.classList.contains('water-delivery-cart-select-quantity')) return;
  settleChecked = false;
  invalidateCart();
  schedule(1100); // let the theme's own update land first
});

const observed = new WeakSet();
function observeProducts() {
  document.querySelectorAll('[data-products]').forEach((el) => {
    if (observed.has(el)) return;
    observed.add(el);
    new MutationObserver(() => {
      invalidateCart(); // the line list moved: our snapshot is now stale
      if (busy) { dirtyWhileBusy = true; return; } // coalesce; drained in reconcile's finally
      settleChecked = false;
      schedule(600);
    }).observe(el, { childList: true, subtree: true });
  });
}

async function reconcile() {
  if (busy) return;
  busy = true;
  try {
    const cart = await cartRead(true);
    // A rate-limited read is not "the cart is empty": stand down and let the
    // next mutation or the settle pass judge it. Writing off a null here is how
    // a 429 would turn into a wrong deposit correction.
    if (!cart) return;
    updateRenewal(cart);
    noteCart(cart); // drawer SUBTOTAL from /cart.js, the figure checkout charges

    let waterQty = 0;
    let hasDispenser = false;
    const depositLines = [];
    for (const item of cart.items) {
      if (item.handle === WATER_HANDLE) waterQty += item.quantity;
      else if (DEPOSIT_HANDLES.some((h) => item.handle.indexOf(h) === 0)) depositLines.push(item);
      if (item.product_id === DISPENSER_PRODUCT_ID) hasDispenser = true;
    }
    syncOffer(cart, waterQty, hasDispenser);
    const primary = depositLines.find((l) => l.variant_id === DEPOSIT_VARIANT_ID) || depositLines[0] || null;

    /* Never correct deposits off a single snapshot. The legacy theme's own
     * quantity sequence edits the DEPOSIT line first and the WATER line second
     * (~1-3s apart), so a mid-sequence read (old water, new deposits) looks
     * like a mismatch that is about to resolve itself - and a write here
     * re-asserts the stale quantity (observed live 2026-07-17: downward edits
     * ended water 4 / deposits 6). Rule: re-read after 1200ms and write only
     * if the water quantity is UNCHANGED across both reads; if water moved,
     * the theme is mid-sequence - stand down and let the settle pass judge
     * the final state. */
    let changed = false;
    const mismatched = depositLines.some((l) => l.quantity !== (l === primary ? waterQty : 0));
    if (mismatched) {
      await new Promise((r) => setTimeout(r, 1200));
      const confirm = await cartRead(true);
      if (!confirm) return;
      let cWaterQty = 0;
      const cDepositLines = [];
      for (const item of confirm.items) {
        if (item.handle === WATER_HANDLE) cWaterQty += item.quantity;
        else if (DEPOSIT_HANDLES.some((h) => item.handle.indexOf(h) === 0)) cDepositLines.push(item);
      }
      if (cWaterQty === waterQty) {
        const cPrimary = cDepositLines.find((l) => l.variant_id === DEPOSIT_VARIANT_ID) || cDepositLines[0] || null;
        for (const line of cDepositLines) {
          const want = line === cPrimary ? cWaterQty : 0; // one deposit line, matched to jugs
          if (line.quantity !== want && breakerOk()) {
            await change(line.key, want);
            changed = true;
          }
        }
      } else {
        settleChecked = false; // water moved between reads: theme mid-sequence
      }
    }
    if (!primary && waterQty > 0) {
      // "Water present, deposits absent" is also the transient middle of the
      // theme's own remove-water sequence (it zeroes the deposit line first).
      // Confirm with a second read before adding, so we never re-add deposits
      // into a cart that is about to lose its water line.
      await new Promise((r) => setTimeout(r, 1200));
      const confirmCart = await cartRead(true);
      if (!confirmCart) return;
      let cWater = 0;
      let cDeposit = 0;
      for (const item of confirmCart.items) {
        if (item.handle === WATER_HANDLE) cWater += item.quantity;
        else if (DEPOSIT_HANDLES.some((h) => item.handle.indexOf(h) === 0)) cDeposit += item.quantity;
      }
      if (cWater > 0 && cDeposit === 0 && breakerOk()) {
        const n = awNet();
        const items = [{ id: DEPOSIT_VARIANT_ID, quantity: cWater }];
        // Adds are not idempotent: go through addItems, which re-reads before it
        // ever re-sends, so a lost response cannot double the deposit line.
        if (n) await n.addItems(items, confirmCart);
        else await cartPost('/cart/add.js', { items: items });
        changed = true;
      }
    }

    if (changed) {
      // The theme rebuilds its own markup (and rebinds listeners); the rebuild
      // mutates [data-products], which re-runs this reconcile as a final check.
      cartAnnounce();
      const fresh = await cartRead(true);
      if (fresh) { updateRenewal(fresh); noteCart(fresh); }
    } else if (!settleChecked) {
      // The theme's own deposit adjustment can land AFTER the last drawer
      // re-render (no DOM signal). One delayed verification pass per burst
      // catches that late write; a second clean pass ends the burst.
      settleChecked = true;
      schedule(2500);
    }
  } catch (_) {
    /* non-blocking; checkout enforcement still owns correctness */
  } finally {
    busy = false;
    if (dirtyWhileBusy) {
      dirtyWhileBusy = false;
      settleChecked = false;
      schedule(600); // re-renders that landed mid-pass get their own pass
    }
    gateButtons();
  }
}

/* Checkout-button gate. The legacy theme ships TWO competing checkDepositAgree
 * implementations: a global one that only reads the DRAWER terms box
 * (#CartTermsDrawer) but fires on every .checkAgreeJS change, and a CartForm one
 * that only reads #DepositAgree but runs only on cart re-renders. Net effect on
 * the /cart page: checking the deposit box DISABLES the button (the global
 * handler consults the hidden drawer's unchecked terms box) until a quantity
 * change forces a re-render (observed live 2026-07-17, arm B day-1 smoke).
 * One rule instead, evaluated per surface and always LAST (microtask after the
 * legacy handlers): a checkout button is enabled iff every agreement checkbox
 * rendered in ITS OWN surface (drawer vs page form) is checked; no checkboxes
 * rendered means nothing to agree to. */
function gateButtons() {
  document.querySelectorAll('button.cart__checkout').forEach((btn) => {
    const scope = btn.closest('#CartDrawer') || btn.closest('form') || document;
    const boxes = Array.from(new Set(scope.querySelectorAll('input.cart__terms-checkbox, input.checkAgreeJS')));
    const ok = boxes.every((b) => b.checked);
    btn.disabled = !ok;
    btn.classList.toggle('cart-checkout-button-disabled', !ok);
  });
}

document.addEventListener('change', (e) => {
  const t = e.target;
  if (!t || !t.matches || !t.matches('input.cart__terms-checkbox, input.checkAgreeJS')) return;
  setTimeout(gateButtons, 0); // after the legacy handlers, so this state wins
});

function change(key, quantity) {
  // Absolute quantity, therefore idempotent: safe for the transport to retry.
  return cartPost('/cart/change.js', { id: key, quantity });
}

/* Keep the auto-applied first-order discount matched to the cart when
 * quantities change after add to cart. offerCode() mirrors aw-order-widget.js
 * (codes starter22 / home77 / family33; jugs + dispenser count toward the
 * minimums, deposits and shelf never do) - keep the two in sync. Only runs
 * when the widget armed an offer (aw_offer_code in localStorage), so carts
 * built outside the offer flow are never touched. */
/* The only discount codes this theme owns. Anything else attached to the cart
 * belongs to the customer (a typed promo) or a referral/ambassador link
 * (LeadDyno / UpPromote); the cart API's discount field REPLACES the whole code
 * set, so a blind write strips it and kills that order's attribution and the
 * partner's commission. Fix 2026-07-20. */
const THEME_CODES = ['starter22', 'home77', 'family33'];

function offerCode(waterQty, hasDispenser) {
  const eligible = waterQty + (hasDispenser ? 1 : 0);
  if (waterQty < 1) return null;
  if (waterQty <= 3) return eligible >= 3 ? 'starter22' : null;
  /* 2026-08-18 (Brian): home77 extended from <=5 to <=6 so the chart-matched
   * 6-jug default keeps the FREE DISPENSER as the go-to offer. Verified in
   * admin: home77 is a Buy-X-Get-Y (buy 4+ water jugs -> dispenser 100% off,
   * no upper limit) so a 6-jug cart validates; at exactly 6+dispenser it also
   * pays the customer more than family33 ($77.00 vs $76.89). family33 (33%,
   * admin minimum 7 items) now starts at 8 jugs, or 7+ eligible items.
   * starter22 stays the sub-4-jug offer, unchanged. */
  if (waterQty <= 6) return hasDispenser && eligible >= 5 ? 'home77' : null;
  return eligible >= 7 ? 'family33' : null;
}

function syncOffer(cart, waterQty, hasDispenser) {
  // Only touch carts the widget armed, same opt-in as B.
  let armed = null;
  let ineligible = null;
  try {
    armed = localStorage.getItem('aw_offer_code');
    ineligible = localStorage.getItem('aw_offer_ineligible');
  } catch (_) { return; }
  if (!armed) return;
  // Eligibility gate. All three theme codes are new-customers-only and
  // one-use-per-customer, so a returning customer must never have one
  // re-attached off a stale aw_offer_code. This file ships on the drawer and is
  // handed no liquid data blob, so it cannot read customerEligible itself; the
  // widget, which can, writes the durable aw_offer_ineligible marker when it
  // suppresses the offer. Contract: marker present = ineligible (stand down
  // completely: do not attach, do not subtract), marker absent = eligible.
  // Fix 2026-07-21.
  if (ineligible) return;
  const want = offerCode(waterQty, hasDispenser) || 'none';
  // C fork: the CART's attached codes are the truth (localStorage lied once:
  // a failed write left it claiming the swap already happened, so every later
  // pass stood down). Compare against cart.discount_codes and re-assert until
  // the cart actually matches; update localStorage only after a 2xx.
  const attached = (cart.discount_codes || []).map((d) => String(d.code || '').toLowerCase());
  // Never clobber a code we did not put there (see THEME_CODES). If the cart
  // carries a customer-entered or referral code, stand down completely: the
  // customer's code wins and their attribution survives.
  if (attached.some((c) => THEME_CODES.indexOf(c) === -1)) return;
  const matches = want === 'none' ? attached.length === 0 : (attached.length === 1 && attached[0] === want);
  if (matches) {
    try { localStorage.setItem('aw_offer_code', want); } catch (_) {}
    return;
  }
  // want === 'none' writes discount:'' on purpose: that is the ONLY path that
  // DETACHES a theme code the cart has stopped qualifying for (drop 8 jugs to 4
  // and family33 must come off, or it rides to checkout and pays out a discount
  // the summary never quoted). Safe because the THEME_CODES check above already
  // stood us down on any cart carrying a code we do not own. Revert 2026-07-21.
  cartPost('/cart/update.js', { discount: want === 'none' ? '' : want }).then((r) => {
    if (r && r.ok) { try { localStorage.setItem('aw_offer_code', want); } catch (_) {} }
  }).catch(() => {});
}

/* Renewal line: always the published curve for CA/FL/TX water, on the 4 bottle
 * minimum (trial sizes 1 to 3 continue on the 4 bottle plan). Other regions bill
 * their flat variant price until their curves are published. */
const CURVE = { 4: 2600, 6: 2500, 8: 2400, 10: 2350, 12: 2300 };
function curvePer(qty) {
  if (CURVE[qty]) return CURVE[qty];
  if (qty > 12) return 2300;
  return 2600;
}

/* Route minimums (keep in sync with ROUTE_MIN_QTY in aw-guided-flow.js):
 * San Diego renews at 8 jugs, everywhere else at the standard 4
 * (Brian, 2026-08-18). The route rides the 'Delivery Cadence' cart
 * attribute the wizard writes on every add, e.g. 'once every 3 weeks
 * (San Diego)', so pre-existing carts resolve correctly too. */
const ROUTE_MIN_QTY = { 'San Diego': 8 };
function cartMinQty(cart) {
  const cad = (cart.attributes && cart.attributes['Delivery Cadence']) || '';
  for (const k in ROUTE_MIN_QTY) if (cad.indexOf('(' + k + ')') > -1) return ROUTE_MIN_QTY[k];
  return 4;
}

function updateRenewal(cart) {
  let total = 0;
  let planName = '';
  const minQ = cartMinQty(cart);
  for (const item of cart.items) {
    if (!item.selling_plan_allocation) continue;
    if (item.handle === WATER_HANDLE) {
      const eff = Math.max(item.quantity, minQ);
      const onCurve = (item.variant_title || '').indexOf('CA/FL/TX') > -1;
      const per = onCurve ? curvePer(eff) : item.selling_plan_allocation.price;
      total += per * eff;
    } else {
      total += item.selling_plan_allocation.price * item.quantity;
    }
    planName = item.selling_plan_allocation.selling_plan.name || planName;
  }
  const show = total > 0;
  if (show) {
    const text = usd(total) + ' ' + planName.replace(/^Deliver every/i, 'every').toLowerCase();
    document.querySelectorAll('[data-aw-recurring]').forEach((el) => { el.textContent = text; });
  }
  document.querySelectorAll('[data-aw-recurring-wrap], [data-aw-recurring-note]').forEach((el) => { el.hidden = !show; });
}

/* Cart drawer open/close race (2026-08-06, Brian on mobile: "if you click on it
 * once, it will appear and then disappear").
 *
 * theme.Drawers.open() ends by calling bindEvents(), which attaches the
 * click-outside-to-close listener to WINDOW - synchronously, inside the very
 * event dispatch that opened the drawer. Per the DOM spec a listener added to an
 * ancestor the event has not reached yet still receives that event, so the
 * opening click carries straight on to window and closes the drawer it just
 * opened. open() only calls stopPropagation() when it is handed the event AND
 * the drawer was closed, so every open that does not carry its event is exposed.
 * Two such paths are live on this store: theme.CartDrawer opens on 'cart:open'
 * and on 'ajaxProduct:added' via this.drawer.open() with NO event, and two
 * installed apps dispatch 'cart:open' from inside a click - Appstle Bundles
 * (appstle-bundles-trigger-cart-refresh) and LB Upsell (lb-utils.js).
 * Traced live 2026-08-06, identical on E8 and on LIVE E10: one click produced
 * open -> drawerOpen -> that same click reaching window -> close.
 *
 * Fix: let the theme bind its closer exactly as it always did, then hold back
 * that ONE listener until the current event has finished dispatching. Nothing
 * else about bindEvents changes - the ESC handler and the mobile scroll lock
 * still run synchronously, and unbindEvents still removes the listener through
 * the theme's own window.namespaces bookkeeping (window.on stores one callback
 * per "type.namespace" key, so the re-arm has to reuse that same key).
 *
 * Deliberately NOT changed: tapping the cart icon while the drawer is open still
 * closes it. That is a different branch - open() returns early on this.isOpen,
 * so the click reaches the already-armed closer - and it is the toggle customers
 * expect. Verified as a separate trace before this fix was written.
 */
function guardDrawerCloseRace() {
  const P = window.theme && window.theme.Drawers && window.theme.Drawers.prototype;
  if (!P) return false;
  if (P.__awCloseRaceGuarded) return true;
  const original = P.bindEvents;
  if (typeof original !== 'function' || typeof window.on !== 'function' || typeof window.off !== 'function') return false;
  P.__awCloseRaceGuarded = true;
  P.bindEvents = function () {
    const self = this;
    const result = original.apply(this, arguments);
    const key = 'click' + this.config.namespace; // e.g. "click.drawer-cart"
    const closer = window.namespaces && window.namespaces[key];
    if (!closer) return result; // theme changed shape: leave its behaviour alone
    window.off(key); // detach the closer the theme just armed mid-dispatch
    setTimeout(function () {
      // A close that lands before this fires (isOpen false) must not re-arm it,
      // or the listener would outlive its unbindEvents and leak onto window.
      if (self.isOpen) window.on(key, closer);
    }, 0);
    return result;
  };
  return true;
}

/* Stale cart line key on a side-cart removal (2026-08-07, Brian on mobile: "I
 * attempted to remove items from it in the mobile version, and it will show a
 * zero value, but the items are still reflecting").
 *
 * MEASURED on E8 at 393x852, plan-only path (no accessory added). Shopify
 * RE-KEYS a cart line whenever its discount allocation changes, and
 * applyOffer() attaches home77 AFTER the drawer has already been rendered from
 * the pre-discount cart. Captured, one run:
 *
 *   /cart.js keys   32526818082867:36162ea8...   50650537263386:985f8a9c...
 *   drawer keys     32526818082867:3660afa3...   50650537263386:1caacf7e...
 *
 * Both dead. theme.js's changeItem() posts {id: <key>} - handleWaterDeliveryChange
 * forces cartLine to -1, so the line-index path is never taken - and the edge
 * answers:
 *
 *   POST /cart/change.js {"quantity":0,"id":"50650537263386:1caacf7e..."}
 *   -> 400 {"status":"bad_request","message":"no valid id or line parameter"}
 *
 * theme.js only alerts on 422, so the 400 is swallowed. Worse, the theme's own
 * water sequence zeroes the DEPOSIT line FIRST and then calls
 * adjustRefundableJugQuantity(), which finds water 4 / deposits 0 and ADDS THE
 * DEPOSITS BACK. Net effect of one removal: the select flicks to "0 jugs", four
 * cart writes go out, and the cart ends byte-for-byte where it started - 9
 * items, $236.00. The items are still there. That is Brian's report exactly.
 *
 * Fix: repair the key instead of failing. On a 400/404 from a change that was
 * posted with a LINE KEY, re-read the cart, find the line by variant id and
 * re-issue with the key the cart actually holds. Only when EXACTLY ONE line
 * carries that variant: two lines of the same variant cannot be told apart from
 * a dead key, and guessing there would remove the wrong line and the wrong
 * money. Calls made with a bare variant id (the theme's own deposit
 * adjustments) are left alone - those never go stale.
 *
 * The sequencing is also fixed at source in aw-guided-flow.js, which now
 * announces a cart:build after the discount write so the drawer re-renders with
 * live keys. This guard is the belt to that braces, and it also covers re-keys
 * caused by the installed apps, which we do not control. */
function guardStaleLineKey() {
  const C = window.theme && window.theme.cart;
  if (!C) return false;
  if (C.__awStaleKeyGuarded) return true;
  const original = C.changeItem;
  if (typeof original !== 'function') return false;
  C.__awStaleKeyGuarded = true;
  C.changeItem = function (key, qty, cartLine) {
    const self = this;
    return Promise.resolve(original.call(this, key, qty, cartLine)).then(function (body) {
      // Only a line key can go stale, and only a rejected change is worth retrying.
      if (typeof key !== 'string' || key.indexOf(':') === -1) return body;
      let parsed = null;
      try { parsed = JSON.parse(body); } catch (_) { return body; }
      if (!parsed || (parsed.status !== 400 && parsed.status !== 404)) return body;
      const variantId = key.split(':')[0];
      return cartRead(true).then(function (cart) {
        if (!cart || !cart.items) return body;
        const matches = cart.items.filter((i) => String(i.variant_id) === variantId);
        if (matches.length !== 1) return body; // ambiguous: never guess which line
        if (matches[0].key === key) return body; // key was fine; the failure is something else
        return original.call(self, matches[0].key, qty, -1);
      }, function () { return body; });
    });
  };
  return true;
}

/* theme.js defines theme.Drawers and theme.cart, and the header section
 * instantiates the cart drawer on its own onLoad, so neither may exist when this
 * module runs. The patches only have to be in place before a human can open the
 * drawer, so a short poll is ample; give up quietly rather than spin. */
(function armGuards(tries) {
  const drawerArmed = guardDrawerCloseRace();
  const keyArmed = guardStaleLineKey();
  if (drawerArmed && keyArmed) return;
  if (tries > 60) return; // ~6s
  setTimeout(function () { armGuards(tries + 1); }, 100);
})(0);

function boot() {
  observeProducts();
  watchSubtotal();
  gateButtons();
  cartRead().then((c) => { if (c) { updateRenewal(c); noteCart(c); } }).catch(() => {});
  // Heal any pre-existing deposit mismatch on load - a cart left wrong by an
  // interrupted edit sequence must not survive a page view. Each page view is
  // a fresh breaker budget; reconcile no-ops on clean carts.
  settleChecked = false;
  schedule(1500);
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
